// apps/web/src/layout/LeftPanel/ChatThread.tsx — streaming chat UI.
// Messages persist in chatStore (localStorage) keyed by projectId.
// System prompt is built from the project's memory bank and injected on every request.
import { useRef, useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useChatStore } from '../../lib/store/chatStore';
import { useRunStore } from '../../lib/store/runStore';
import { useAuthStore } from '../../lib/store/authStore';
import { useProjectStore, buildSystemPrompt } from '../../lib/store/projectStore';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3007';

// ── Realtime subscription ──────────────────────────────────────────────────────

interface RealtimeMessage {
  type: string;
  level?: string;
  message?: string;
  summary?: string;
  error?: string;
}

async function trySubscribeToRun(
  runId:   string,
  onEvent: (msg: RealtimeMessage) => void,
): Promise<(() => void) | null> {
  const url  = import.meta.env['VITE_SUPABASE_URL'] as string | undefined;
  const key  = import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined;
  if (!url || !key) return null;

  try {
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = createClient(url, key);
    const channel  = supabase.channel(`run:${runId}`);

    channel.on('broadcast', { event: '*' }, ({ payload }: { payload: RealtimeMessage }) => {
      onEvent(payload);
    });

    await channel.subscribe();
    return () => { void supabase.removeChannel(channel); };
  } catch {
    return null;
  }
}

// ── Tool-event formatting ──────────────────────────────────────────────────────

/** Extract the `path` field from a tool-call JSON-arg blob, tolerant of partial streams. */
function extractPath(argsJson: string): string | null {
  if (!argsJson) return null;
  try {
    const parsed = JSON.parse(argsJson) as { path?: unknown };
    return typeof parsed.path === 'string' ? parsed.path : null;
  } catch {
    // Fallback for partial JSON — grab "path":"…"
    const m = argsJson.match(/"path"\s*:\s*"([^"]+)"/);
    return m?.[1] ?? null;
  }
}

/** One-line status line, tolerant of partial args. */
function formatToolStart(name: string, argsJson: string): string {
  const path = extractPath(argsJson);
  if (name === 'gen_image') {
    // Extract prompt for a descriptive label
    let promptHint = '';
    try {
      const p = (JSON.parse(argsJson) as { prompt?: string }).prompt ?? '';
      promptHint = p.length > 50 ? p.slice(0, 50) + '…' : p;
    } catch {
      const m = argsJson.match(/"prompt"\s*:\s*"([^"]{0,50})/);
      promptHint = m?.[1] ?? '';
    }
    return `🎨 Generating image${promptHint ? `: "${promptHint}"` : '…'}`;
  }
  const label =
    name === 'write_file'  ? `📝 Writing ${path ?? '…'}` :
    name === 'read_file'   ? `👀 Reading ${path ?? '…'}` :
    name === 'delete_file' ? `🗑 Deleting ${path ?? '…'}` :
    name === 'list_files'  ? '📂 Listing workspace files' :
    `⚙ ${name}${path ? ` ${path}` : ''}`;
  return label;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ChatThread() {
  // Use the current project's message thread
  const currentProjectId          = useProjectStore((s) => s.currentProjectId);
  const currentProject            = useProjectStore((s) => s.projects[s.currentProjectId]);
  const messages                  = useChatStore((s) => s.messagesByProject[currentProjectId] ?? []);
  const { addMessage, appendToLast } = useChatStore();
  const { activeRun, selectedProvider, selectedModel } = useRunStore();
  const [draft, setDraft]         = useState('');
  const [sending, setSending]     = useState(false);
  const bottomRef                 = useRef<HTMLDivElement>(null);
  const textareaRef               = useRef<HTMLTextAreaElement>(null);
  const unsubRef                  = useRef<(() => void) | null>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Subscribe to Realtime when run becomes active
  useEffect(() => {
    const runId = activeRun?.id;
    if (!runId) return;

    addMessage(currentProjectId, {
      role:    'assistant',
      content: `▶ Run started — goal: "${activeRun.goal ?? 'unknown'}"`,
      runId,
    });

    void trySubscribeToRun(runId, (msg: RealtimeMessage) => {
      if (msg.type === 'log' && msg.message) {
        appendToLast(currentProjectId, `\n[${(msg.level ?? 'info').toUpperCase()}] ${msg.message}`);
      } else if (msg.type === 'completed' && msg.summary) {
        addMessage(currentProjectId, { role: 'assistant', content: `✓ Completed: ${msg.summary}`, runId });
      } else if (msg.type === 'failed' && msg.error) {
        addMessage(currentProjectId, { role: 'assistant', content: `✗ Failed: ${msg.error}`, runId });
      } else if (msg.type === 'paused') {
        addMessage(currentProjectId, { role: 'assistant', content: '⏸ Run paused.', runId });
      }
    }).then((unsub) => { if (unsub) unsubRef.current = unsub; });

    return () => { unsubRef.current?.(); unsubRef.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRun?.id]);

  async function submit() {
    const text = draft.trim();
    if (!text || sending) return;

    // Last 20 messages as conversation history (exclude system messages)
    const history = messages
      .filter((m) => m.role !== 'system')
      .slice(-20)
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    addMessage(currentProjectId, { role: 'user', content: text, runId: activeRun?.id });
    setDraft('');

    // Optimistically add empty assistant message to stream into
    addMessage(currentProjectId, { role: 'assistant', content: '' });

    try {
      const session = useAuthStore.getState().session;
      const token   = session?.access_token;
      if (!token) {
        appendToLast(currentProjectId, '(Not signed in — please log in again.)');
        return;
      }

      // Build the system prompt from the project's memory bank
      const systemPrompt = buildSystemPrompt(currentProject);

      const res = await fetch(`${API_BASE}/api/chat`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          messages: [
            { role: 'system', content: systemPrompt },
            ...history,
            { role: 'user', content: text },
          ],
          provider:    selectedProvider,
          model:       selectedModel,
          // Tools are keyed to the project: the agent can only write files
          // into the workspace of the currently selected project.
          projectSlug: currentProject?.slug,
          enableTools: !!currentProject,
          projectEnv:  currentProject?.env ?? 'dev',
        }),
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string };
        appendToLast(currentProjectId, `Error: ${err.error ?? 'Unknown error'}`);
        return;
      }

      // Stream SSE deltas into the last assistant message
      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let   buf     = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') break;
          try {
            const event = JSON.parse(raw) as {
              type:     string;
              delta?:   string;
              error?:   string;
              name?:    string;
              args?:    string;
              summary?: string;
              ok?:      boolean;
              id?:      string;
            };
            if (event.type === 'delta' && event.delta) {
              appendToLast(currentProjectId, event.delta);
            } else if (event.type === 'tool_start') {
              // Inline status line: "⚙ write_file src/App.tsx…"
              const hint = formatToolStart(event.name ?? 'tool', event.args ?? '');
              appendToLast(currentProjectId, `\n\n${hint}`);
              // Track last affected file on the project memory bank
              const pathHint = extractPath(event.args ?? '');
              if (pathHint && currentProject) {
                useProjectStore.getState().addAffectedFile(currentProject.id, pathHint);
              }
            } else if (event.type === 'tool_result') {
              const icon = event.ok ? '✓' : '✗';
              appendToLast(currentProjectId, `\n  ${icon} ${event.summary ?? ''}`);
            } else if (event.type === 'error') {
              appendToLast(currentProjectId, `\n\n⚠ ${event.error}`);
            }
          } catch { /* malformed SSE line */ }
        }
      }
    } catch (err) {
      appendToLast(
        currentProjectId,
        `\n\nNetwork error: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!sending) { setSending(true); void submit().finally(() => setSending(false)); }
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!sending) { setSending(true); void submit().finally(() => setSending(false)); }
  }

  const isRunning = sending || activeRun?.status === 'running' || activeRun?.status === 'planning';
  const projectLabel = currentProject ? currentProject.name : 'No project';

  return (
    <div className="abw-chat" aria-label="Chat thread">
      {/* Project context chip */}
      <div
        style={{
          padding: '0 var(--space-3) var(--space-2)',
          fontSize: '0.6875rem',
          color: 'var(--text-tertiary)',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
        }}
        title={`Memory bank: ${currentProject?.id ?? 'global'}`}
      >
        <span aria-hidden>🧠</span>
        <span>{projectLabel}</span>
        {currentProject && (
          <span style={{ color: 'var(--accent-500)', marginLeft: 'auto' }}>
            {currentProject.memory.completedTasks.length > 0
              ? `${currentProject.memory.completedTasks.length} tasks done`
              : 'Fresh start'}
          </span>
        )}
      </div>

      {/* Active run banner */}
      {isRunning && (
        <div className="abw-chat__run-banner" role="status" aria-live="polite">
          <span className="abw-chat__run-dot" aria-hidden />
          Run in progress
        </div>
      )}

      {/* Message list */}
      <div className="abw-chat__messages" role="log" aria-live="polite" aria-label="Messages">
        {messages.length === 0 && (
          <div style={{
            color: 'var(--text-secondary)', fontSize: '0.8125rem',
            textAlign: 'center', padding: 'var(--space-4) 0',
          }}>
            <div style={{ fontSize: '1.5rem', opacity: 0.3, marginBottom: 'var(--space-2)' }}>💬</div>
            <div style={{ fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
              No messages yet
            </div>
            <div>Describe what you want to build.</div>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`abw-chat__msg abw-chat__msg--${msg.role}`}>
            <div className="abw-chat__bubble">{msg.content}</div>
          </div>
        ))}
        <div ref={bottomRef} aria-hidden />
      </div>

      {/* Input row */}
      <form className="abw-chat__input-row" onSubmit={onSubmit} aria-label="Send message">
        <textarea
          ref={textareaRef}
          className="abw-chat__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            sending    ? 'Thinking…' :
            isRunning  ? 'Run in progress…' :
            'Describe what to build… (Enter to send)'
          }
          rows={1}
          aria-label="Message input"
          disabled={isRunning}
        />
        <button
          type="submit"
          disabled={!draft.trim() || isRunning}
          style={{
            height: 36, padding: '0 var(--space-3)',
            border: 'none', borderRadius: 'var(--radius-button)',
            background: 'var(--accent-500)', color: '#fff',
            cursor: draft.trim() && !isRunning ? 'pointer' : 'default',
            opacity: draft.trim() && !isRunning ? 1 : 0.4,
            fontWeight: 600, fontSize: '0.8125rem', flexShrink: 0,
            transition: 'opacity var(--duration-fast) var(--ease-standard)',
          }}
          aria-label="Send"
        >
          Send
        </button>
      </form>
    </div>
  );
}
